// Remove / Install a component on the aircraft Components tab — the pure half
// (component editor, 2026-10-07; Aaron: "need a remove component - copy from legacy", R5 the
// empty slot stays, R6 Install in this round). The API (Aeronet.app component_lifecycle.py):
//   POST /v6/aircraft/{id}/components/{item}/remove/preview   what Remove will do, no write
//   POST /v6/aircraft/{id}/components/{item}/remove           one transaction
//   POST /v6/aircraft/{id}/components/{item}/install          one transaction
//   GET  /v6/aircraft/{id}/components/installable              what Install may fit
//
// Legacy's removal popup (mod_aircraft/js/aircraft_component_removal.js:574-845): a removal
// type (Serviceable 16 / Unserviceable 16-2 / Overhaul 17 / Retire-Scrap 18), a required
// reason and location, condition, store, shelf, the Date of Values and the readings at it.

import type { WorkOrderTerm } from "../hooks/useWorkOrderTerm";

export const REMOVAL_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "16", label: "Remove — serviceable" },
  { value: "16-2", label: "Remove — unserviceable" },
  { value: "17", label: "Remove — for overhaul" },
  { value: "18", label: "Retire / scrap" },
];

/** The removal popup's "Process Overhaul Job" options (removal.js:695-719; type 17 only),
 * in the tenant's word for a job (useWorkOrderTerm). The values are what the API takes. */
export function overhaulJobOptions(term: WorkOrderTerm): ReadonlyArray<{ value: string; label: string }> {
  return [
    { value: "Task_Install", label: "Add task for installation" },
    { value: "Outwork", label: `Add outwork to ${term.shortSingular}` },
  ];
}

const OVERHAUL_JOB_OPTION_VALUES = ["Task_Install", "Outwork"];

export const OVERHAUL_REMOVAL = "17";

export interface RemovalFormValues {
  remove_status: string;
  reason: string;
  condition: string;
  location: string;
  store: string;
  shelf: string;
  /** "YYYY-MM-DD" */
  date_of_values: string;
  /** Based On → the reading at the Date of Values; blank = the aircraft's current one. */
  readings: Record<string, string>;
  /** "Process Overhaul Job" ticked (sent only for a removal for overhaul). */
  overhaul_job?: boolean;
  /** "new" = Create New Job, else an open job's id. */
  overhaul_job_id?: string;
  overhaul_options?: string[];
}

export interface RemovalSummary {
  components: Array<{
    item_id: number;
    part_number: string | null;
    serial_number: string | null;
    /** what the tab calls it (item_descrp, else the part's name; API 2026-10-12) */
    description?: string | null;
    status: number;
    limitations: Array<{ limitation_id: number; based_on: string | null; tsn_at_removal: unknown; tso_at_removal: unknown }>;
    logs: Array<{ table: string; log_id: number }>;
    special_tasks: number;
    leaves_parent: boolean;
    /** the API resolves statuses by name; `retired` says it is a retirement whatever the id */
    retired?: boolean;
  }>;
  component_count: number;
  limitation_count: number;
  log_count: number;
  special_task_count: number;
  overhaul_job?: { job_id: number | null; create: boolean; options: string[] } | null;
}

const isDate = (b: string) => b.includes("Date");
const CONDITION = ["On Condition", "Not Applicable", "Untracked"];

/** The axes a reading is asked for: the unit axes among these Based On values, once each. */
export function readingAxes(basedOns: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  for (const raw of basedOns) {
    const b = (raw ?? "").trim();
    if (!b || isDate(b) || CONDITION.includes(b) || out.includes(b)) continue;
    out.push(b);
  }
  return out;
}

/** Only the readings typed: a blank one is left to the API (the aircraft's current value). */
export function typedReadings(readings: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(readings).filter(([, v]) => String(v ?? "").trim() !== ""));
}

export function removalPayload(values: RemovalFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    remove_status: values.remove_status,
    reason: values.reason.trim(),
    condition: values.condition || null,
    location: values.location,
    store: values.store || null,
    shelf: values.shelf || null,
    date_of_values: values.date_of_values,
    readings: typedReadings(values.readings),
  };
  if (values.remove_status === OVERHAUL_REMOVAL && values.overhaul_job) {
    const id = Number(values.overhaul_job_id);
    payload.overhaul_job = {
      job_id: values.overhaul_job_id && values.overhaul_job_id !== "new" && id > 0 ? id : null,
      options: (values.overhaul_options ?? []).filter((o) => OVERHAUL_JOB_OPTION_VALUES.includes(o)),
    };
  }
  return payload;
}

/** The jobs the overhaul list offers: legacy lists the aircraft's open jobs whose to_stock is 3
 * (removal.js:723-726). */
export function overhaulJobs<J extends { to_stock?: number | null }>(jobs: J[]): J[] {
  return jobs.filter((j) => Number(j.to_stock) === 3);
}

export function removalFormErrors(values: RemovalFormValues): Partial<Record<keyof RemovalFormValues, string>> {
  const errors: Partial<Record<keyof RemovalFormValues, string>> = {};
  if (!values.reason.trim()) errors.reason = "A reason for removal is required";
  if (!values.location) errors.location = "Removal location is required";
  if (!values.date_of_values) errors.date_of_values = "Date of Values is required";
  return errors;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A sub-component in the confirm: its part number, else its description, and its S/N; "—" when
 * it has none of them (a check with no part, mustang 2026-10-11: "comes off with it: ."). */
export function subComponentLabel(c: RemovalSummary["components"][number]): string {
  const name = String(c.part_number || c.description || "").trim();
  const sn = String(c.serial_number || "").trim();
  return [name, sn && `S/N ${sn}`].filter(Boolean).join(" ") || "—";
}

/** The confirm dialog's lines, from the dry-run preview — what the write WILL do. */
export function removalConfirmLines(
  summary: RemovalSummary,
  registration: string | null | undefined,
  term: WorkOrderTerm
): string[] {
  const [top, ...subs] = summary.components;
  const retire = top?.retired ?? top?.status === 18;
  const lines = [
    `${retire ? "Retire" : "Remove"} ${top?.part_number ?? "component"} S/N ${top?.serial_number || "N/A"} from ${registration || "the aircraft"}.`,
    `${plural(summary.limitation_count, "limitation")} come off; the slot stays on the aircraft, empty.`,
  ];
  const verb = (n: number, one: string, many: string) => (n === 1 ? one : many);
  if (summary.log_count) {
    lines.push(`${plural(summary.log_count, "inspection / AD line")} ${verb(summary.log_count, "freezes", "freeze")} with the component.`);
  }
  if (summary.special_task_count) {
    lines.push(`${plural(summary.special_task_count, "special task")} ${verb(summary.special_task_count, "closes", "close")}.`);
  }
  if (subs.length) {
    lines.push(
      `${plural(subs.length, "sub-component")} come${subs.length === 1 ? "s" : ""} off with it: ${subs
        .map(subComponentLabel)
        .join(", ")}.`
    );
  }
  if (top?.leaves_parent) lines.push("It leaves its assembly.");
  const job = summary.overhaul_job;
  if (job) {
    // Only the verb is lower-cased: "add outwork to W/O", never "w/o".
    const extras = overhaulJobOptions(term)
      .filter((o) => job.options.includes(o.value))
      .map((o) => o.label.charAt(0).toLowerCase() + o.label.slice(1));
    const target = job.create
      ? `A new overhaul ${term.shortSingular} for ${registration || "the aircraft"}`
      : `${term.shortSingular} #${job.job_id}`;
    lines.push(`${target} takes the component${extras.length ? `, with: ${extras.join(", ")}` : ""}.`);
  }
  return lines;
}

export interface InstallFormValues {
  item_id: number | null;
  date_of_values: string;
  position: string;
  readings: Record<string, string>;
}

/** A component the Sub Components tab's link search may offer: legacy's serial search takes
 * status 7 / 16 items not on an aircraft (mod_aircraft/includes/ajax_parts.html:28-33). The
 * aircraft Install sheet searches server-side instead (GET .../installable). */
export function installable(item: { status?: number | null; aircraft_obj?: unknown; aircraft_id?: number | null }): boolean {
  const status = Number(item.status);
  const onAircraft = Boolean(item.aircraft_obj) || Number(item.aircraft_id) > 0;
  return (status === 7 || status === 16) && !onAircraft;
}

/** One off-aircraft limit of a component the search found (GET .../installable). */
export interface InstallCandidateLimit {
  id: number;
  based_on: string | null;
  limitation_type: string | null;
  life: string | number | null;
  life_hours: string | number | null;
  tsn_at_install: string | number | null;
  tsn_unknown: boolean;
  tso_at_install: string | number | null;
}

export interface InstallCandidate {
  id: number;
  serial_number: string | null;
  status: number;
  part: { id: number; number: string | null; name: string | null };
  limitations: InstallCandidateLimit[];
}

/** TSN / TSO at install, per off-aircraft limit id, as typed. */
export type InstallFigures = Record<number, { tsn: string; tso: string; tsnUnknown: boolean }>;

const fig = (v: string | number | null | undefined) => (v === null || v === undefined ? "" : String(v));

/** Legacy's fill-slots prefill: each limit's TSN / TSO as it came off (aircraft_fill_slots.inc:386-389). */
export function initialFigures(candidate: InstallCandidate): InstallFigures {
  const out: InstallFigures = {};
  for (const l of candidate.limitations) {
    out[l.id] = { tsn: fig(l.tsn_at_install), tso: fig(l.tso_at_install), tsnUnknown: Boolean(l.tsn_unknown) };
  }
  return out;
}

/** A TSN / TSO edit. On a Limited Life limit the TSO is the TSN (legacy hides TSO and copies it,
 * aircraft_fill_slots.inc:531-536). */
export function editFigure(
  figures: InstallFigures,
  limit: InstallCandidateLimit,
  key: "tsn" | "tso" | "tsnUnknown",
  value: string | boolean
): InstallFigures {
  const current = figures[limit.id] ?? { tsn: "", tso: "", tsnUnknown: false };
  const next = { ...current, [key]: value } as InstallFigures[number];
  if (key === "tsn" && limit.limitation_type === "Limited Life") next.tso = String(value);
  return { ...figures, [limit.id]: next };
}

/** Only the figures changed from what the component came off with; the API carries the rest. */
export function installFiguresPayload(candidate: InstallCandidate | null, figures: InstallFigures) {
  if (!candidate) return [];
  const before = initialFigures(candidate);
  const out: Array<Record<string, unknown>> = [];
  for (const l of candidate.limitations) {
    const now = figures[l.id];
    const was = before[l.id];
    if (!now || !was) continue;
    const entry: Record<string, unknown> = { limitation_id: l.id };
    if (now.tsnUnknown !== was.tsnUnknown) entry.tsn_unknown = now.tsnUnknown;
    if (!now.tsnUnknown && now.tsn.trim() !== was.tsn && now.tsn.trim() !== "") entry.tsn_at_install = now.tsn.trim();
    if (now.tso.trim() !== was.tso && now.tso.trim() !== "") entry.tso_at_install = now.tso.trim();
    if (Object.keys(entry).length > 1) out.push(entry);
  }
  return out;
}

/** A figure that is not a number of zero or more. */
export function installFigureErrors(figures: InstallFigures): string | null {
  for (const f of Object.values(figures)) {
    for (const v of [f.tsnUnknown ? "" : f.tsn, f.tso]) {
      if (v.trim() !== "" && !(Number(v) >= 0)) return "TSN / TSO at install must be a number of 0 or more";
    }
  }
  return null;
}

export function installPayload(
  values: InstallFormValues,
  slotLimitationId?: number | null,
  limitations: Array<Record<string, unknown>> = []
): Record<string, unknown> {
  return {
    date_of_values: values.date_of_values,
    position: values.position.trim() ? values.position.trim() : null,
    slot_limitation_id: slotLimitationId ?? null,
    readings: typedReadings(values.readings),
    limitations,
  };
}

/** The search's params: legacy's item_list, limited to the slot's part number unless the user
 * widens it (ajax_parts.html:28-45; the server applies status / fitted / sub-component). */
export function installSearchParams(by: "serial" | "number", text: string, slotPartNumber?: string | null, anyPart = false) {
  const params: Record<string, string | number> = { q: text.trim(), by, limit: 20 };
  if (slotPartNumber && !anyPart) params.part_number = slotPartNumber;
  return params;
}

export const todayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
